import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    CircularProgress,
    Alert,
} from '@mui/material';
import { Fp1Device } from '../fp1.types';
import { setFp1DeviceSubscriptionOverride } from '../fp1.api';

export type OverrideType = 'comp' | 'invoiced' | 'none';

interface Props {
    open: boolean;
    device: Fp1Device | null;
    onClose: () => void;
    /** Called after a successful override so the parent can refresh device + custody data. */
    onSaved: () => void;
    /**
     * Open straight on one tab. Used by the "Remove subscription status" shortcut button so
     * the admin does not have to find the Remove tab first.
     */
    initialType?: OverrideType;
}

const Fp1SubscriptionOverrideDialog: React.FC<Props> = ({
    open,
    device,
    onClose,
    onSaved,
    initialType = 'comp',
}) => {
    const [type, setType] = useState<OverrideType>(initialType);
    // Comp fields
    const [compedBy, setCompedBy] = useState('');
    const [compReason, setCompReason] = useState('');
    // Invoice fields
    const [invoiceId, setInvoiceId] = useState('');
    const [invoiceEndDate, setInvoiceEndDate] = useState('');
    // Removal field (optional)
    const [removeReason, setRemoveReason] = useState('');

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            setType(initialType);
            setCompedBy('');
            setCompReason('');
            setInvoiceId('');
            setInvoiceEndDate('');
            setRemoveReason('');
            setError(null);
            setSaving(false);
        }
    }, [open, initialType]);

    const compValid = compedBy.trim() !== '' && compReason.trim() !== '';
    const invoiceValid = invoiceId.trim() !== '' && invoiceEndDate.trim() !== '';
    // Removal needs no input — the reason is optional.
    const canSave = type === 'comp' ? compValid : type === 'invoiced' ? invoiceValid : true;

    const handleSave = async () => {
        if (!device || !canSave) return;
        setSaving(true);
        setError(null);
        try {
            if (type === 'comp') {
                await setFp1DeviceSubscriptionOverride(device.uuid, {
                    type: 'comp',
                    comped_by: compedBy.trim(),
                    comp_reason: compReason.trim(),
                });
            } else if (type === 'invoiced') {
                await setFp1DeviceSubscriptionOverride(device.uuid, {
                    type: 'invoiced',
                    external_invoice_id: invoiceId.trim(),
                    external_invoice_end_date: invoiceEndDate,
                });
            } else {
                await setFp1DeviceSubscriptionOverride(device.uuid, {
                    type: 'none',
                    ...(removeReason.trim() !== '' ? { reason: removeReason.trim() } : {}),
                });
            }
            onSaved();
            onClose();
        } catch (err: any) {
            console.error('Failed to set subscription override', err);
            setError(
                err?.response?.data?.message ||
                    'Failed to update subscription status. Please try again.',
            );
        } finally {
            setSaving(false);
        }
    };

    if (!device) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 600 }}>
                Set subscription status — {device.serial_number}
            </DialogTitle>
            <DialogContent dividers>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 2 }}
                >
                    Manually override this pod&apos;s subscription status, or use{' '}
                    <strong>Remove</strong> to strip every subscription status from it. The change
                    is recorded in the device&apos;s chain of custody.
                </Typography>

                {device.subscription_cleared_at ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        This pod&apos;s subscription status was removed on{' '}
                        {new Date(device.subscription_cleared_at).toLocaleDateString()}. Setting
                        Comp or Invoice below will replace that.
                    </Alert>
                ) : null}

                <ToggleButtonGroup
                    exclusive
                    fullWidth
                    color="primary"
                    value={type}
                    onChange={(_e, val) => {
                        if (val) setType(val as OverrideType);
                    }}
                    size="small"
                    sx={{ mb: 3 }}
                >
                    <ToggleButton value="comp">Comp</ToggleButton>
                    <ToggleButton value="invoiced">Invoice</ToggleButton>
                    <ToggleButton value="none">Remove</ToggleButton>
                </ToggleButtonGroup>

                {type === 'none' ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Alert severity="warning">
                            This removes <strong>every</strong> subscription status from the pod —
                            comp, any external invoice, and the pod&apos;s own Stripe subscription
                            link. The pod will show as <strong>No Sub</strong> and stops counting
                            against its organization&apos;s subscription seats, even when that
                            organization has an active subscription. The pod itself stays put —
                            nothing is returned, detached or unassigned.
                        </Alert>
                        <TextField
                            label="Why (optional)"
                            value={removeReason}
                            onChange={(e) => setRemoveReason(e.target.value)}
                            fullWidth
                            multiline
                            minRows={2}
                            placeholder="e.g. Athlete cancelled their combo subscription; keeping the pod with them for now."
                            helperText="Recorded in the pod's chain of custody."
                        />
                    </Box>
                ) : type === 'comp' ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Your name"
                            value={compedBy}
                            onChange={(e) => setCompedBy(e.target.value)}
                            fullWidth
                            size="small"
                            placeholder="Who is comping this device"
                        />
                        <TextField
                            label="Why it's comped & for how long"
                            value={compReason}
                            onChange={(e) => setCompReason(e.target.value)}
                            fullWidth
                            multiline
                            minRows={3}
                            placeholder="e.g. Comped for the Santee demo unit through end of the 2026 season."
                        />
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Stripe invoice number"
                            value={invoiceId}
                            onChange={(e) => setInvoiceId(e.target.value)}
                            fullWidth
                            size="small"
                            placeholder="e.g. INV-0042 or in_1A2b3C..."
                        />
                        <TextField
                            label="Invoiced through"
                            type="date"
                            value={invoiceEndDate}
                            onChange={(e) => setInvoiceEndDate(e.target.value)}
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            helperText="Date this invoice covers the pod through."
                        />
                    </Box>
                )}

                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color={type === 'none' ? 'error' : 'primary'}
                    onClick={handleSave}
                    disabled={saving || !canSave}
                >
                    {saving ? (
                        <CircularProgress size={20} />
                    ) : type === 'none' ? (
                        'Remove subscription status'
                    ) : (
                        'Save'
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default Fp1SubscriptionOverrideDialog;
